import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Calculator,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Download,
  FileSearch,
  History,
  Keyboard,
  Landmark,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import {
  AppLink,
  ArgentineDateInput,
  CopyableDate,
  CopyableMoney,
  DataTable,
  DataTableAction,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTablePrimaryCell,
  DataTableRow,
  DataTableStatus,
  DataTableValue,
  FeedbackBanner,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import { SentenceInterestImporter } from "../components/SentenceInterestImporter.jsx";
import {
  displayedTrancheStart,
  nextIsoDate,
} from "../lib/interest-dates.js";

function todayInput() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Mendoza",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function freshManualTranches() {
  return [{
    id: crypto.randomUUID(),
    calculationType: "",
    rateCode: undefined,
    from: "",
    to: todayInput(),
  }];
}

function manualDraftInput(capital, concept, tranches) {
  return {
    mode: "manual",
    capital: String(capital ?? ""),
    concept,
    manualTranches: tranches.map((tranche) => ({
      calculationType: tranche.calculationType || "",
      ...(tranche.rateCode === undefined
        ? {}
        : { rateCode: tranche.rateCode }),
      from: apiDate(tranche.from),
      to: apiDate(tranche.to),
    })),
  };
}

function apiDate(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function displayDate(value) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function DisplayedDate({ as = "button", value }) {
  return (
    <CopyableDate as={as} value={value}>
      {displayDate(value)}
    </CopyableDate>
  );
}

function inputDate(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const [day, month, year] = value.split("/");
  return `${year}-${month}-${day}`;
}

function currency(value) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function Money({ as = "button", value }) {
  return (
    <CopyableMoney as={as} value={value}>
      {currency(value)}
    </CopyableMoney>
  );
}

function percentage(value) {
  return `${new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value || 0)}%`;
}

function numericCapital(value) {
  const normalized = String(value || "").trim();
  if (normalized.includes(",")) {
    return Number(normalized.replace(/\./g, "").replace(",", "."));
  }
  return Number(normalized);
}

const RATE_DISPLAY_NAMES = {
  0: "Banco Nación Activa",
  1: "Libre 36 meses · Fuera de uso",
  2: "Libre 60 meses · Fuera de uso",
  3: "Libre 72 meses · Ley 9516",
  4: "Banco Nación Pasiva",
  5: "Ley 4087",
  6: "Unidad de Valor Adquisitivo (UVA)",
};

function calculationStatus(status) {
  if (status === "approved") return { label: "Aprobado", tone: "success" };
  if (status === "reviewed") return { label: "Revisado", tone: "warning" };
  return { label: "Borrador", tone: "neutral" };
}

function calculationProgress(calculation) {
  if (calculation.analysis?.status === "preparing") return "Guardando archivos";
  if (calculation.analysis?.status === "queued") return "En cola";
  if (calculation.analysis?.status === "processing") return "Analizando";
  if (calculation.analysis?.status === "failed") return "Error de análisis";
  if (
    calculation.analysis?.status === "ready"
    && calculation.draftDirty
  ) return "Listo para revisar";
  if (calculation.version > 0) return `Versión ${calculation.version}`;
  if (calculation.analysis?.status === "ready") return "Listo para revisar";
  return "Sin calcular";
}

const viewDescriptions = {
  history: "Consultá, reabrí y administrá todos los cálculos que hiciste.",
  choose: "Elegí cómo querés ingresar los datos del nuevo cálculo.",
  manual: "Ingresá el capital, el período y las tasas para realizar una nueva liquidación.",
  sentences: "Importá una sentencia, identificá sus rubros y revisá la liquidación propuesta.",
};

export function JudicialInterestCalculatorPage({
  view = "history",
  calculationId = null,
  sentenceStep = null,
}) {
  const api = useApi();
  const { navigate } = useRouter();
  const [rates, setRates] = useState([]);
  const [manualTranches, setManualTranches] = useState(freshManualTranches);
  const [openInterestGroups, setOpenInterestGroups] = useState({});
  const [capital, setCapital] = useState("");
  const [concept, setConcept] = useState("");
  const [result, setResult] = useState(null);
  const [currentCalculation, setCurrentCalculation] = useState(null);
  const [loadedView, setLoadedView] = useState(null);
  const [loadingCalculation, setLoadingCalculation] = useState(Boolean(calculationId));
  const [draftSaveState, setDraftSaveState] = useState("idle");
  const [pendingDraft, setPendingDraft] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
  });
  const [historyFilters, setHistoryFilters] = useState({
    query: "",
    status: "all",
    archived: false,
    page: 1,
  });
  const [historyBusyId, setHistoryBusyId] = useState(null);
  const [loadingRates, setLoadingRates] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [error, setError] = useState("");
  const workspaceRef = useRef(null);
  const manualDraftCreationRef = useRef(null);
  const lastSavedDraftRef = useRef(null);
  const draftSaveChainRef = useRef(Promise.resolve());
  const activeView = calculationId ? loadedView : view;
  const isSentenceResultStep = activeView === "sentences"
    && sentenceStep === "resultado";

  useEffect(() => {
    if (activeView !== "manual" || rates.length) return undefined;
    let active = true;
    setLoadingRates(true);
    api.listJudicialInterestRates()
      .then((rateData) => {
        if (!active) return;
        setRates(rateData);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoadingRates(false);
      });
    return () => {
      active = false;
    };
  }, [activeView, api, rates.length]);

  useEffect(() => {
    if (activeView !== "history") return undefined;
    let active = true;
    api.listJudicialInterestHistory({ ...historyFilters, limit: 10 })
      .then((historyData) => {
        if (!active) return;
        const items = Array.isArray(historyData) ? historyData : historyData.items;
        setHistory(items);
        setHistoryPagination(Array.isArray(historyData)
          ? { page: 1, pages: 1, total: items.length }
          : historyData.pagination);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      });
    return () => { active = false; };
  }, [activeView, api, historyFilters]);

  useEffect(() => {
    if (!calculationId) {
      setLoadingCalculation(false);
      setLoadedView(null);
      setCurrentCalculation(null);
      return undefined;
    }
    let active = true;
    setLoadingCalculation(true);
    setError("");
    api.getJudicialInterestCalculation(calculationId)
      .then((calculation) => {
        if (!active) return;
        restoreCalculation(calculation);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoadingCalculation(false);
      });
    return () => {
      active = false;
    };
  }, [api, calculationId]);

  useEffect(() => {
    if (!calculationId || loadingCalculation || !currentCalculation || !activeView) return;
    if (activeView === "manual" && sentenceStep) {
      navigate(`/calculadora-intereses/${calculationId}`, { replace: true });
      return;
    }
    if (activeView !== "sentences") return;
    const nextStep = result ? "resultado" : "rubros";
    if (!sentenceStep || (sentenceStep === "resultado" && !result)) {
      navigate(`/calculadora-intereses/${calculationId}/${nextStep}`, { replace: true });
    }
  }, [
    activeView,
    calculationId,
    currentCalculation,
    loadingCalculation,
    navigate,
    result,
    sentenceStep,
  ]);

  useEffect(() => {
    if (calculationId || view !== "manual") {
      return undefined;
    }
    let active = true;
    const draftInput = manualDraftInput(
      capital,
      concept,
      manualTranches,
    );
    setLoadingCalculation(true);
    manualDraftCreationRef.current ||= api.createManualJudicialInterestDraft(
      draftInput,
    );
    manualDraftCreationRef.current
      .then((calculation) => {
        if (!active) return;
        restoreCalculation(calculation);
        navigate(`/calculadora-intereses/${calculation.id}`, { replace: true });
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoadingCalculation(false);
      });
    return () => {
      active = false;
    };
  }, [api, calculationId, capital, concept, manualTranches, navigate, view]);

  useEffect(() => {
    if (!calculationId && view !== "manual") {
      manualDraftCreationRef.current = null;
    }
  }, [calculationId, view]);

  useEffect(() => {
    if (
      activeView !== "manual"
      || !currentCalculation
      || loadingCalculation
      || calculating
    ) return;
    const draftInput = manualDraftInput(capital, concept, manualTranches);
    const serialized = JSON.stringify(draftInput);
    if (serialized === lastSavedDraftRef.current) return;
    setPendingDraft({ kind: "manual", draftInput, serialized });
  }, [
    activeView,
    calculating,
    capital,
    concept,
    currentCalculation,
    loadingCalculation,
    manualTranches,
  ]);

  useEffect(() => {
    if (!currentCalculation || !pendingDraft) return undefined;
    const timer = window.setTimeout(() => {
      persistPendingDraft(pendingDraft).catch(() => {});
    }, 600);
    return () => window.clearTimeout(timer);
  }, [currentCalculation, pendingDraft]);

  useEffect(() => {
    const analysisStatus = currentCalculation?.analysis?.status;
    if (
      !calculationId
      || currentCalculation?.kind !== "sentence_batch"
      || !["preparing", "queued", "processing"].includes(analysisStatus)
    ) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const calculation = await api.getJudicialInterestCalculation(calculationId);
        if (active) restoreCalculation(calculation);
      } catch (requestError) {
        if (active) setError(requestError.message);
      }
    };
    const timer = window.setInterval(poll, 2_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [api, calculationId, currentCalculation?.analysis?.status, currentCalculation?.kind]);

  function startNewCalculation(nextView) {
    setCapital("");
    setConcept("");
    setManualTranches(freshManualTranches());
    setOpenInterestGroups({});
    setResult(null);
    setCurrentCalculation(null);
    setPendingDraft(null);
    manualDraftCreationRef.current = null;
    lastSavedDraftRef.current = null;
    setError("");
    navigate(`/calculadora-intereses/nuevo/${nextView}`);
  }

  function persistPendingDraft(draft = pendingDraft) {
    if (!currentCalculation || !draft) return Promise.resolve();
    setDraftSaveState("saving");
    const operation = draftSaveChainRef.current
      .catch(() => {})
      .then(() => api.updateJudicialInterestDraft(
        currentCalculation.id,
        draft.kind,
        draft.draftInput,
      ))
      .then(() => {
        lastSavedDraftRef.current = draft.serialized;
        setPendingDraft((current) => (
          current?.serialized === draft.serialized ? null : current
        ));
        setDraftSaveState("saved");
      })
      .catch((saveError) => {
        setDraftSaveState("error");
        setError(saveError.message);
        throw saveError;
      });
    draftSaveChainRef.current = operation;
    return operation;
  }

  async function leaveToHistory() {
    try {
      if (pendingDraft) await persistPendingDraft(pendingDraft);
      navigate("/calculadora-intereses");
    } catch {
      // The error banner explains why navigation was stopped.
    }
  }

  async function refreshHistory() {
    const historyData = await api.listJudicialInterestHistory({
      ...historyFilters,
      limit: 10,
    });
    setHistory(Array.isArray(historyData) ? historyData : historyData.items);
    if (!Array.isArray(historyData)) setHistoryPagination(historyData.pagination);
  }

  async function applyHistoryAction(calculation, action) {
    if (historyBusyId) return;
    setHistoryBusyId(calculation.id);
    setError("");
    try {
      await action(calculation.id);
      await refreshHistory();
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setHistoryBusyId(null);
    }
  }

  function restoreCalculation(calculation) {
    const { input, result: storedResult } = calculation;
    setCurrentCalculation(calculation);
    setPendingDraft(null);
    setDraftSaveState("idle");
    if (calculation.kind === "sentence_batch" || storedResult?.kind === "sentence_batch") {
      setLoadedView("sentences");
      setResult(calculation.draftDirty ? null : storedResult);
      lastSavedDraftRef.current = calculation.draftInput
        ? JSON.stringify(calculation.draftInput)
        : null;
      setError("");
      return;
    }
    setLoadedView("manual");
    const editableInput = calculation.draftInput || input || {};
    setCapital(String(editableInput.capital ?? ""));
    setConcept(editableInput.concept || "");
    const inputTranches = editableInput.manualTranches?.length
      ? editableInput.manualTranches
      : [{
          calculationType:
            editableInput.mode === "manual" ? "rate" : editableInput.mode || "",
          rateCode: editableInput.rateCode,
          from: editableInput.from,
          to: editableInput.to || todayInput(),
        }];
    if (inputTranches.length) {
      const restored = inputTranches.map((tranche) => ({
        id: crypto.randomUUID(),
        calculationType: tranche.calculationType,
        rateCode: tranche.rateCode === undefined ? undefined : String(tranche.rateCode),
        from: inputDate(tranche.from),
        to: inputDate(tranche.to),
      }));
      setManualTranches(restored);
      setOpenInterestGroups(Object.fromEntries(restored.map((tranche) => [
        tranche.id,
        tranche.calculationType === "rate" ? "rates" : "debts",
      ])));
      lastSavedDraftRef.current = JSON.stringify(manualDraftInput(
        editableInput.capital ?? "",
        editableInput.concept || "",
        restored,
      ));
    }
    setResult(calculation.draftDirty ? null : storedResult);
    setError("");
  }

  function openHistoryCalculation(calculation) {
    if (calculation.kind === "sentence_batch") {
      navigate(`/calculadora-intereses/${calculation.id}/${
        calculation.result && !calculation.draftDirty ? "resultado" : "rubros"
      }`);
      return;
    }
    navigate(`/calculadora-intereses/${calculation.id}`);
  }

  function editSentenceDebtManually(debt) {
    const restored = (debt.manualTranches || debt.tranches || []).map((tranche) => ({
      id: crypto.randomUUID(),
      calculationType: tranche.calculationType || "rate",
      rateCode:
        tranche.rateCode === undefined || tranche.rateCode === null
          ? undefined
          : String(tranche.rateCode),
      from: inputDate(tranche.from),
      to: inputDate(tranche.to),
    }));
    setCapital(String(debt.capital ?? ""));
    setConcept(debt.label || "");
    setManualTranches(restored);
    setOpenInterestGroups(Object.fromEntries(restored.map((tranche) => [
      tranche.id,
      tranche.calculationType === "rate" ? "rates" : "debts",
    ])));
    setResult(null);
    setError("");
    manualDraftCreationRef.current = null;
    navigate("/calculadora-intereses/nuevo/manual");
    window.requestAnimationFrame(() => {
      workspaceRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  function continueFromConsolidatedResult(consolidatedResult) {
    const trancheId = crypto.randomUUID();
    setCapital(String(consolidatedResult.total ?? ""));
    setConcept(consolidatedResult.case?.title
      ? `Continuación · ${consolidatedResult.case.title}`
      : "Continuación de liquidación");
    setManualTranches([{
      id: trancheId,
      calculationType: "",
      rateCode: undefined,
      from:
        consolidatedResult.cutoffDate
        || consolidatedResult.calculationDate
        || "",
      to: todayInput(),
    }]);
    setOpenInterestGroups({});
    setResult(null);
    setError("");
    manualDraftCreationRef.current = null;
    navigate("/calculadora-intereses/nuevo/manual");
    window.requestAnimationFrame(() => {
      workspaceRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  const manualOptions = useMemo(() => ({
    debts: [{
      value: "value-debt",
      label: "Deuda de valor",
      description: "Aplica automáticamente la Tasa Ley 4087 durante todo el período.",
    },
    {
      value: "money-debt",
      label: "Deuda de dinero",
      description: "Selecciona automáticamente las tasas correspondientes y divide el cálculo según su vigencia.",
    }],
    rates: rates.map((rate) => ({
      value: `rate:${rate.code}`,
      label: RATE_DISPLAY_NAMES[rate.code] || rate.name,
      description: "Aplica esta tasa durante todo el período.",
    })),
  }), [rates]);
  const resultGroups = result?.kind === "sentence_batch"
    ? result.outcomes?.length
      ? result.outcomes.flatMap((outcome) => [
          ...outcome.debts.map((debt) => ({
            ...debt,
            id: `${outcome.key}:${debt.id}`,
            label: `${debt.label} · ${outcome.label}`,
            outcomeKey: outcome.key,
          })),
          ...(outcome.projection ? [{
            ...outcome.projection,
            id: `${outcome.key}:post-judgment-projection`,
            label: `${outcome.label} · Intereses desde primera instancia`,
            outcomeKey: outcome.key,
            isProjection: true,
          }] : []),
        ])
      : [
          ...result.debts,
          ...(result.projection ? [{ ...result.projection, isProjection: true }] : []),
        ]
    : result
      ? [{ id: "manual", label: null, ...result }]
      : [];
  const sentenceBreakdown = result?.kind === "sentence_batch"
    ? {
        firstInstanceInterest: result.debts.reduce(
          (sum, debt) => sum + Number(debt.interest || 0),
          0,
        ),
        projectionInterest: result.outcomes?.length
          ? result.outcomes.reduce(
              (sum, outcome) => sum + Number(outcome.projection?.interest || 0),
              0,
            )
          : Number(result.projection?.interest || 0),
      }
    : null;

  function updateManualTranche(id, changes) {
    setManualTranches((current) => {
      const changedIndex = current.findIndex((tranche) => tranche.id === id);
      return current.map((tranche, index) => {
        if (index === changedIndex) return { ...tranche, ...changes };
        if (changes.to !== undefined && index === changedIndex + 1) {
          return { ...tranche, from: nextIsoDate(changes.to) };
        }
        return tranche;
      });
    });
    setResult(null);
  }

  async function exportPdf() {
    setExportingPdf(true);
    setError("");
    try {
      const { downloadJudicialInterestPdf } = await import("../lib/interest-pdf.js");
      downloadJudicialInterestPdf(result);
    } catch (exportError) {
      setError(exportError.message || "No pudimos generar el PDF.");
    } finally {
      setExportingPdf(false);
    }
  }

  function changeManualCalculation(id, value) {
    if (value.startsWith("rate:")) {
      updateManualTranche(id, {
        calculationType: "rate",
        rateCode: value.slice(5),
      });
      return;
    }
    updateManualTranche(id, { calculationType: value, rateCode: undefined });
  }

  async function submit(event) {
    event.preventDefault();
    const missingInterest = manualTranches.find((tranche) => !tranche.calculationType);
    if (missingInterest) {
      setError("Elegí una deuda o una tasa para calcular los intereses.");
      setOpenInterestGroups((current) => ({
        ...current,
        [missingInterest.id]: "debts",
      }));
      return;
    }
    setCalculating(true);
    setError("");
    setResult(null);
    try {
      if (pendingDraft) await persistPendingDraft(pendingDraft);
      await draftSaveChainRef.current;
      const input = {
        mode: "manual",
        manualTranches: manualTranches.map((tranche) => ({
          calculationType: tranche.calculationType,
          ...(tranche.calculationType === "rate"
            ? { rateCode: Number(tranche.rateCode) }
            : {}),
          from: apiDate(tranche.from),
          to: apiDate(tranche.to),
        })),
        capital: numericCapital(capital),
        concept,
      };
      const calculation = currentCalculation
        ? await api.recalculateJudicialInterest(currentCalculation.id, input)
        : await api.calculateJudicialInterest(input);
      setPendingDraft(null);
      lastSavedDraftRef.current = JSON.stringify(manualDraftInput(
        capital,
        concept,
        manualTranches,
      ));
      setCurrentCalculation(calculation);
      setResult(calculation.result);
      if (!calculationId) {
        navigate(`/calculadora-intereses/${calculation.id}`);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCalculating(false);
    }
  }

  return (
    <div className={`page interest-calculator-page ${result && activeView === "manual" ? "has-liquidation-dock" : ""}`}>
      <PageHeader
        hero
        eyebrow="Instrumenta · Cálculos"
        title="Calculadora de Intereses"
        description={currentCalculation
          ? isSentenceResultStep
            ? "Revisá el resultado consolidado y cada tramo del cálculo en su detalle auditable."
            : currentCalculation.version > 0
              ? `Editá la versión ${currentCalculation.version}. Al recalcular, conservaremos las versiones anteriores.`
              : "Continuá este borrador cuando quieras. Guardamos los cambios automáticamente."
          : viewDescriptions[activeView] || viewDescriptions.history}
        actions={activeView === "history" ? (
          <button
            className="primary-button interest-new-button"
            type="button"
            onClick={() => navigate("/calculadora-intereses/nuevo")}
          >
            <Plus size={17} />
            Nuevo cálculo
          </button>
        ) : (
          <button
            className="secondary-button interest-history-button"
            type="button"
            onClick={leaveToHistory}
          >
            <History size={17} />
            Volver al historial
          </button>
        )}
      />

      {error && <FeedbackBanner tone="error">{error}</FeedbackBanner>}

      {currentCalculation && !loadingCalculation && (
        <FeedbackBanner tone="warning">
          {currentCalculation.version > 0
            ? `Estás viendo la versión ${currentCalculation.version}. Los cambios se guardarán como una versión nueva al recalcular.`
            : "Podés salir y volver más tarde: el cálculo quedará guardado para que continúes cuando quieras."}
          {draftSaveState === "saving" && " Guardando cambios…"}
          {draftSaveState === "saved" && " Cambios guardados."}
        </FeedbackBanner>
      )}

      {result && activeView === "manual" && (
        <aside
          className="interest-consolidated-dock"
          aria-label={result.kind === "sentence_batch"
            ? "Liquidación consolidada"
            : "Liquidación manual"}
        >
          <div className="interest-consolidated-dock-title">
            <span className="eyebrow">
              {result.kind === "sentence_batch"
                ? "Liquidación consolidada"
                : "Liquidación manual"}
            </span>
            <small>
              <CalendarDays size={14} />
              {result.kind === "sentence_batch" ? (
                <>
                  Al{" "}
                  <DisplayedDate value={result.cutoffDate || result.calculationDate} />
                </>
              ) : (
                <>
                  <DisplayedDate value={result.from} /> al{" "}
                  <DisplayedDate value={result.to} />
                </>
              )}
            </small>
          </div>
          <dl>
            <div>
              <dt>Total</dt>
              <dd><Money value={result.total} /></dd>
            </div>
            <div>
              <dt>Capital</dt>
              <dd><Money value={result.capital} /></dd>
            </div>
            <div>
              <dt>Intereses</dt>
              <dd><Money value={result.interest} /></dd>
            </div>
            <div>
              <dt>{result.kind === "sentence_batch" ? "Rubros" : "Días"}</dt>
              <dd>{result.kind === "sentence_batch" ? result.debts.length : result.days}</dd>
            </div>
          </dl>
          {result.kind === "sentence_batch" && (
            <button
              className="continue-interest-calculation"
              type="button"
              onClick={() => continueFromConsolidatedResult(result)}
            >
              Continuar cálculo
              <ArrowRight size={15} />
            </button>
          )}
        </aside>
      )}

      <div className="interest-dashboard-content" ref={workspaceRef}>
      {loadingCalculation ? (
        <section className="legal-resource-loading" aria-live="polite">
          <RefreshCw className="spin" size={22} />
          <strong>Cargando cálculo…</strong>
        </section>
      ) : activeView === "history" ? (
        <section
          className="interest-history-section interest-history-browser"
          data-justiniano-target="interest-history"
        >
          <div className="interest-history-heading">
            <div>
              <span className="eyebrow">Tus cálculos</span>
              <h2>Historial de cálculos</h2>
            </div>
            <span>{historyPagination.total} cálculos conservados</span>
          </div>
          <div className="interest-history-filters">
            <label>
              <Search size={16} />
              <input
                type="search"
                placeholder="Buscar concepto, CUIJ u operador"
                value={historyFilters.query}
                onChange={(event) => setHistoryFilters((current) => ({
                  ...current,
                  query: event.target.value,
                  page: 1,
                }))}
              />
            </label>
            <select
              value={historyFilters.status}
              onChange={(event) => setHistoryFilters((current) => ({
                ...current,
                status: event.target.value,
                page: 1,
              }))}
            >
              <option value="all">Todos los estados</option>
              <option value="draft">Borradores</option>
              <option value="reviewed">Revisados</option>
              <option value="approved">Aprobados</option>
            </select>
            <select
              value={String(historyFilters.archived)}
              onChange={(event) => setHistoryFilters((current) => ({
                ...current,
                archived: event.target.value === "true",
                page: 1,
              }))}
            >
              <option value="false">Historial activo</option>
              <option value="true">Archivados</option>
            </select>
          </div>
          {history.length ? (
            <DataTable
              ariaLabel="Historial de cálculos de intereses"
              className="interest-history-table"
              density="compact"
              minWidth="900px"
            >
              <DataTableHead>
                <DataTableRow>
                  <DataTableHeaderCell>Cálculo</DataTableHeaderCell>
                  <DataTableHeaderCell>Total</DataTableHeaderCell>
                  <DataTableHeaderCell>Estado</DataTableHeaderCell>
                  <DataTableHeaderCell priority="secondary">Última modificación</DataTableHeaderCell>
                  <DataTableHeaderCell align="end">Acciones</DataTableHeaderCell>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {history.map((calculation) => {
                  const status = calculationStatus(calculation.status);
                  const updatedAt = new Intl.DateTimeFormat("es-AR", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(calculation.updatedAt || calculation.createdAt));
                  return (
                    <DataTableRow
                      key={calculation.id}
                      label={`Abrir ${calculation.title}`}
                      onActivate={() => openHistoryCalculation(calculation)}
                    >
                      <DataTableCell>
                        <DataTablePrimaryCell
                          icon={<History size={16} />}
                          eyebrow={`${calculation.kind === "sentence_batch"
                            ? "Desde sentencia"
                            : "Manual"} · ${calculationProgress(calculation)}`}
                          title={calculation.title}
                          meta={`Operador ${calculation.createdByName || "Usuario"}`}
                        />
                      </DataTableCell>
                      <DataTableCell>
                        <DataTableValue
                          primary={calculation.result
                            ? <Money as="span" value={calculation.result.total} />
                            : "Sin calcular"}
                        />
                      </DataTableCell>
                      <DataTableCell>
                        <DataTableStatus label={status.label} tone={status.tone} />
                      </DataTableCell>
                      <DataTableCell priority="secondary">
                        <DataTableValue
                          primary={(
                            <CopyableDate
                              as="span"
                              value={calculation.updatedAt || calculation.createdAt}
                            >
                              {updatedAt}
                            </CopyableDate>
                          )}
                        />
                      </DataTableCell>
                      <DataTableCell align="end">
                        <div className="interest-history-table-actions">
                          {calculation.permissions?.canReview && (
                            <DataTableAction
                              label={`Revisar ${calculation.title}`}
                              disabled={historyBusyId === calculation.id}
                              onClick={() => applyHistoryAction(
                                calculation,
                                api.reviewJudicialInterestCalculation,
                              )}
                            >
                              Revisar
                            </DataTableAction>
                          )}
                          {calculation.permissions?.canApprove && (
                            <DataTableAction
                              label={`Aprobar ${calculation.title}`}
                              icon={<ShieldCheck size={14} />}
                              disabled={historyBusyId === calculation.id}
                              onClick={() => applyHistoryAction(
                                calculation,
                                api.approveJudicialInterestCalculation,
                              )}
                            >
                              Aprobar
                            </DataTableAction>
                          )}
                          {calculation.report.available && (
                            <DataTableAction
                              label={`Descargar reporte de ${calculation.title}`}
                              icon={<Download size={14} />}
                              onClick={() => api.downloadJudicialInterestReport(
                                calculation.id,
                              )}
                            >
                              Reporte
                            </DataTableAction>
                          )}
                          {!historyFilters.archived
                            && calculation.permissions?.canArchive && (
                            <DataTableAction
                              label={`Archivar ${calculation.title}`}
                              icon={<Trash2 size={14} />}
                              disabled={historyBusyId === calculation.id}
                              onClick={() => applyHistoryAction(
                                calculation,
                                api.archiveJudicialInterestCalculation,
                              )}
                            >
                              Archivar
                            </DataTableAction>
                          )}
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
            </DataTable>
          ) : (
            <div className="interest-history-empty">
              <History size={24} />
              <strong>Todavía no hay cálculos guardados</strong>
              <span>Los resultados aparecerán acá automáticamente.</span>
            </div>
          )}
          {historyPagination.pages > 1 && (
            <div className="interest-history-pagination">
              <button
                type="button"
                disabled={historyFilters.page <= 1}
                onClick={() => setHistoryFilters((current) => ({
                  ...current,
                  page: current.page - 1,
                }))}
              >Anterior</button>
              <span>Página {historyPagination.page} de {historyPagination.pages}</span>
              <button
                type="button"
                disabled={historyFilters.page >= historyPagination.pages}
                onClick={() => setHistoryFilters((current) => ({
                  ...current,
                  page: current.page + 1,
                }))}
              >Siguiente</button>
            </div>
          )}
        </section>
      ) : activeView === "choose" ? (
        <section className="interest-new-calculation" aria-labelledby="interest-new-title">
          <div className="interest-new-heading">
            <span className="eyebrow">Nuevo cálculo</span>
            <h2 id="interest-new-title">¿Cómo querés ingresar los datos?</h2>
            <p>Elegí una opción para abrir el formulario correspondiente.</p>
          </div>
          <div className="interest-new-options">
            <button
              data-justiniano-target="interest-mode-manual"
              type="button"
              onClick={() => startNewCalculation("manual")}
            >
              <span className="interest-new-option-icon"><Keyboard size={23} /></span>
              <span>
                <strong>Manual</strong>
                <small>Ingresá el capital, los períodos y las tasas.</small>
              </span>
              <ArrowRight size={18} />
            </button>
            <button
              data-justiniano-target="interest-mode-sentences"
              type="button"
              onClick={() => startNewCalculation("sentencia")}
            >
              <span className="interest-new-option-icon"><FileSearch size={23} /></span>
              <span>
                <strong>Desde sentencia</strong>
                <small>Importá sentencias e identificá sus rubros con IA.</small>
              </span>
              <ArrowRight size={18} />
            </button>
          </div>
        </section>
      ) : activeView === "sentences" ? (
        <div className="interest-sentence-workflow">
          {currentCalculation && (
            <nav className="interest-workflow-steps" aria-label="Pasos del cálculo desde sentencia">
              <AppLink
                className={`interest-workflow-step ${isSentenceResultStep ? "is-complete" : "is-active"}`}
                activeClassName=""
                exact
                to={`/calculadora-intereses/${currentCalculation.id}/rubros`}
                aria-current={!isSentenceResultStep ? "step" : undefined}
              >
                <span className="interest-workflow-step-number">
                  {isSentenceResultStep ? <CheckCircle2 size={17} /> : "1"}
                </span>
                <span>
                  <small>Paso 1</small>
                  <strong>Rubros identificados</strong>
                  <em>Revisá capitales, fechas y tasas.</em>
                </span>
              </AppLink>
              <span className="interest-workflow-connector" aria-hidden="true" />
              {result ? (
                <AppLink
                  className={`interest-workflow-step ${isSentenceResultStep ? "is-active" : ""}`}
                  activeClassName=""
                  exact
                  to={`/calculadora-intereses/${currentCalculation.id}/resultado`}
                  aria-current={isSentenceResultStep ? "step" : undefined}
                >
                  <span className="interest-workflow-step-number">2</span>
                  <span>
                    <small>Paso 2</small>
                    <strong>Resultado y detalle</strong>
                    <em>Auditá cada tramo de la liquidación.</em>
                  </span>
                </AppLink>
              ) : (
                <span className="interest-workflow-step is-disabled" aria-disabled="true">
                  <span className="interest-workflow-step-number">2</span>
                  <span>
                    <small>Paso 2</small>
                    <strong>Resultado y detalle</strong>
                    <em>Calculá los rubros para habilitar este paso.</em>
                  </span>
                </span>
              )}
            </nav>
          )}

          {!isSentenceResultStep ? (
            <SentenceInterestImporter
              key={`${currentCalculation?.id || "new"}-${currentCalculation?.version ?? 0}`}
              api={api}
              initialCalculation={currentCalculation}
              onChanged={() => setResult(null)}
              onDraftCreated={(calculation) => {
                restoreCalculation(calculation);
                navigate(
                  `/calculadora-intereses/${calculation.id}/rubros`,
                  { replace: true },
                );
              }}
              onDraftChange={(draftInput) => {
                const serialized = JSON.stringify(draftInput);
                if (serialized !== lastSavedDraftRef.current) {
                  setPendingDraft({
                    kind: "sentence_batch",
                    draftInput,
                    serialized,
                  });
                }
              }}
              onBeforeCalculate={async () => {
                if (pendingDraft) await persistPendingDraft(pendingDraft);
                await draftSaveChainRef.current;
              }}
              onAnalysisRetried={restoreCalculation}
              onCalculated={(calculation) => {
                setPendingDraft(null);
                lastSavedDraftRef.current = null;
                setCurrentCalculation(calculation);
                setResult(calculation.result);
                navigate(`/calculadora-intereses/${calculation.id}/resultado`);
              }}
            />
          ) : result?.kind === "sentence_batch" ? (
            <section className="interest-sentence-result-overview" aria-labelledby="interest-result-title">
              <header>
                <div>
                  <span className="eyebrow">Resultado</span>
                  <h2 id="interest-result-title">Liquidación consolidada</h2>
                  <p>{result.case?.title || "Rubros reconocidos en las sentencias"}</p>
                </div>
                <span className="interest-period">
                  <CalendarDays size={16} />
                  Liquidación al <DisplayedDate value={result.cutoffDate || result.calculationDate} />
                </span>
              </header>
              <div className="interest-sentence-result-summary">
                <div className="interest-total">
                  <small>Total actualizado</small>
                  <strong><Money value={result.total} /></strong>
                  <span>{result.debts.length} {result.debts.length === 1 ? "rubro incluido" : "rubros incluidos"}</span>
                </div>
                <dl>
                  <div><dt>Capital</dt><dd><Money value={result.capital} /></dd></div>
                  {result.outcomes?.map((outcome) => (
                    <div key={outcome.key}>
                      <dt>{outcome.label}</dt>
                      <dd><Money value={outcome.total} /></dd>
                    </div>
                  ))}
                  <div className="sentence-summary-interest-row">
                    <dt>
                      Intereses hasta primera instancia
                      <small><DisplayedDate value={result.calculationDate} /></small>
                    </dt>
                    <dd><Money value={sentenceBreakdown.firstInstanceInterest} /></dd>
                  </div>
                  {(result.projection || result.outcomes?.some(
                    ({ projection }) => projection,
                  )) && (
                    <div className="sentence-summary-interest-row">
                      <dt>
                        Intereses desde primera instancia hasta corte
                        <small>
                          <DisplayedDate value={result.calculationDate} /> –{" "}
                          <DisplayedDate value={result.cutoffDate} />
                        </small>
                      </dt>
                      <dd><Money value={sentenceBreakdown.projectionInterest} /></dd>
                    </div>
                  )}
                  <div className="sentence-summary-interest-total">
                    <dt>Total de intereses</dt>
                    <dd><Money value={result.interest} /></dd>
                  </div>
                </dl>
              </div>
              <footer>
                <p>El detalle auditable de cada rubro continúa debajo.</p>
                <button
                  className="continue-interest-calculation"
                  type="button"
                  onClick={() => continueFromConsolidatedResult(result)}
                >
                  Usar este total en un nuevo cálculo
                  <ArrowRight size={16} />
                </button>
              </footer>
            </section>
          ) : null}
        </div>
      ) : activeView === "manual" ? (
      <form className="interest-calculator-layout" onSubmit={submit}>
        <section className="form-card interest-input-card">
          <div className="form-card-title">
            <div>
              <h2>Datos del cálculo</h2>
              <p>Elegí el tipo de deuda o una tasa específica y definí el período.</p>
            </div>
          </div>

          <div className="interest-context-fields">
            <label>
              <span className="field-label">Capital</span>
              <div className="money-input">
                <span>$</span>
                <input
                  data-justiniano-target="interest-capital"
                  inputMode="decimal"
                  value={capital}
                  onChange={(event) => {
                    setCapital(event.target.value);
                    setResult(null);
                  }}
                  placeholder="100.000,00"
                  required
                />
              </div>
            </label>
            <label className="interest-reference-field">
              <span className="field-label">Concepto o referencia</span>
              <input
                value={concept}
                maxLength={200}
                onChange={(event) => {
                  setConcept(event.target.value);
                  setResult(null);
                }}
                placeholder="Ej.: Honorarios · Expte. 12345"
              />
            </label>
          </div>

          <div className={`manual-interest-tranches ${manualTranches.length > 1 ? "has-multiple" : ""}`}>
            {manualTranches.length > 1 && (
              <div className="manual-interest-heading">
                <div>
                  <h3>Tramos personalizados</h3>
                  <p>Todos usan el mismo capital y se liquidan por separado.</p>
                </div>
              </div>
            )}
            {manualTranches.map((tranche, index) => {
              const selectedValue = tranche.calculationType === "rate"
                ? `rate:${tranche.rateCode}`
                : tranche.calculationType;
              const openGroup = openInterestGroups[tranche.id] || null;
              return (
                <div
                  className={`manual-interest-tranche ${manualTranches.length === 1 ? "single" : ""}`}
                  key={tranche.id}
                >
                  {manualTranches.length > 1 && (
                  <div className="manual-tranche-title">
                    <span className="tranche-number">{index + 1}</span>
                    <strong>Tramo personalizado {index + 1}</strong>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={`Eliminar tramo personalizado ${index + 1}`}
                      onClick={() => {
                        setManualTranches((current) => (
                          current.filter(({ id }) => id !== tranche.id)
                        ));
                        setResult(null);
                      }}
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                  )}
                  <div className="interest-calculation-field">
                    <span className="field-label">Intereses</span>
                    <div className="interest-option-accordion">
                      <section className={openGroup === "debts" ? "open" : ""}>
                        <button
                          data-justiniano-target={index === 0 ? "interest-kind" : undefined}
                          type="button"
                          className="interest-option-accordion-trigger"
                          onClick={() => setOpenInterestGroups((current) => ({
                            ...current,
                            [tranche.id]: current[tranche.id] === "debts" ? null : "debts",
                          }))}
                        >
                          <span>
                            <strong>Deuda</strong>
                            <small>La calculadora selecciona las tasas automáticamente.</small>
                          </span>
                          <ChevronDown size={18} />
                        </button>
                        {openGroup === "debts" && (
                          <div className="interest-calculation-option-list">
                        {manualOptions.debts.map((option) => (
                          <label
                            className={`interest-calculation-option ${selectedValue === option.value ? "selected" : ""}`}
                            key={option.value}
                          >
                            <input
                              type="radio"
                              name={`interest-calculation-${tranche.id}`}
                              value={option.value}
                              checked={selectedValue === option.value}
                              onChange={(event) => changeManualCalculation(tranche.id, event.target.value)}
                              required
                            />
                            <span>
                              <strong>{option.label}</strong>
                              <small>{option.description}</small>
                            </span>
                            {selectedValue === option.value && <CheckCircle2 size={17} />}
                          </label>
                        ))}
                          </div>
                        )}
                      </section>
                      <section className={openGroup === "rates" ? "open" : ""}>
                        <button
                          type="button"
                          className="interest-option-accordion-trigger"
                          onClick={() => setOpenInterestGroups((current) => ({
                            ...current,
                            [tranche.id]: current[tranche.id] === "rates" ? null : "rates",
                          }))}
                        >
                          <span>
                            <strong>Tasa</strong>
                            <small>Elegí una tasa específica para todo el período.</small>
                          </span>
                          <ChevronDown size={18} />
                        </button>
                        {openGroup === "rates" && (
                          <div className="interest-calculation-option-list">
                        {manualOptions.rates.map((option) => (
                          <label
                            className={`interest-calculation-option compact ${selectedValue === option.value ? "selected" : ""}`}
                            key={option.value}
                          >
                            <input
                              type="radio"
                              name={`interest-calculation-${tranche.id}`}
                              value={option.value}
                              checked={selectedValue === option.value}
                              onChange={(event) => changeManualCalculation(tranche.id, event.target.value)}
                              required
                            />
                            <span>
                              <strong>{option.label}</strong>
                            </span>
                            {selectedValue === option.value && <CheckCircle2 size={17} />}
                          </label>
                        ))}
                          </div>
                        )}
                      </section>
                      {loadingRates && <small className="interest-options-loading">Cargando tasas…</small>}
                      </div>
                    </div>
                  <div className="manual-tranche-dates">
                    <label>
                      Desde
                      <ArgentineDateInput
                        value={tranche.from}
                        onChange={(value) => updateManualTranche(tranche.id, { from: value })}
                        required
                      />
                    </label>
                    <label>
                      Hasta
                      <ArgentineDateInput
                        value={tranche.to}
                        onChange={(value) => updateManualTranche(tranche.id, { to: value })}
                        required
                      />
                    </label>
                  </div>
                </div>
              );
            })}
            <button
              data-justiniano-target="interest-add-tranche"
              type="button"
              className="secondary-button add-interest-tranche"
              onClick={() => setManualTranches((current) => [
                ...current,
                  {
                    id: crypto.randomUUID(),
                    calculationType: "",
                    rateCode: undefined,
                  from: nextIsoDate(current.at(-1)?.to),
                  to: todayInput(),
                },
              ])}
            >
              <Plus size={17} />
              Agregar tramo personalizado
            </button>
          </div>

          <button
            data-justiniano-target="interest-calculate"
            className="primary-button calculate-interest-button"
            disabled={calculating}
          >
            {calculating ? <RefreshCw className="spin" size={18} /> : <Calculator size={18} />}
            {calculating
              ? "Calculando…"
              : currentCalculation
                ? currentCalculation.version > 0
                  ? "Recalcular y crear versión"
                  : "Calcular intereses"
                : "Calcular intereses"}
          </button>
        </section>

        <aside className="interest-summary-card">
          <span className="eyebrow">Resultado</span>
          {result ? (
            <>
              <div className="interest-total">
                <small>Total actualizado</small>
                <strong><Money value={result.total} /></strong>
              </div>
              <dl>
                <div><dt>Capital</dt><dd><Money value={result.capital} /></dd></div>
                <div><dt>Intereses</dt><dd><Money value={result.interest} /></dd></div>
                <div><dt>Tasa acumulada</dt><dd>{percentage(result.percentage)}</dd></div>
                <div><dt>Días</dt><dd>{result.days}</dd></div>
              </dl>
            </>
          ) : (
            <div className="interest-empty-result">
              <Landmark size={29} />
              <p>Completá los datos para ver el capital actualizado y el detalle del cálculo.</p>
            </div>
          )}
        </aside>
      </form>
      ) : null}

      {result && (activeView === "manual" || isSentenceResultStep) && (
        <section className="interest-detail-section">
          <div className="interest-detail-heading">
            <div>
              <span className="eyebrow">Detalle auditable</span>
              <h2>Cómo se realizó el cálculo</h2>
            </div>
            <div className="interest-detail-actions">
              <span className="interest-period">
                <CalendarDays size={16} />
                {result.kind === "sentence_batch" ? (
                  <>
                    Liquidación al{" "}
                    <DisplayedDate value={result.cutoffDate || result.calculationDate} />
                  </>
                ) : (
                  <>
                    <DisplayedDate value={result.from} /> al{" "}
                    <DisplayedDate value={result.to} />
                  </>
                )}
              </span>
              <button
                className="secondary-button interest-export-button"
                type="button"
                disabled={exportingPdf}
                onClick={exportPdf}
              >
                {exportingPdf
                  ? <RefreshCw className="spin" size={16} />
                  : <Download size={16} />}
                {exportingPdf ? "Generando…" : "Exportar PDF"}
              </button>
            </div>
          </div>

          <div className="interest-result-groups">
            {resultGroups.map((group) => (
              <section className="interest-result-debt" key={group.id}>
                {group.label && (
                  <header className="interest-result-debt-heading">
                    <div>
                      <span className="eyebrow">
                        {group.isProjection
                          ? "Proyección consolidada"
                          : "Rubro"}
                      </span>
                      <h3>{group.label}</h3>
                    </div>
                    <div className="interest-result-debt-actions">
                      <span>
                        Capital <Money value={group.capital} /> · Total{" "}
                        <Money value={group.total} />
                      </span>
                      {result.kind === "sentence_batch" && !group.isProjection && (
                        <button
                          type="button"
                          onClick={() => editSentenceDebtManually(group)}
                        >
                          <Pencil size={14} />
                          Editar manualmente
                        </button>
                      )}
                    </div>
                  </header>
                )}
                <div className="interest-tranches">
            {group.tranches.map((tranche, trancheIndex) => (
              <article className="interest-tranche" key={`${tranche.rateCode}-${tranche.from}`}>
                <header>
                  <span className="tranche-number">{trancheIndex + 1}</span>
                  <div>
                    <h3>{tranche.rateName}</h3>
                    <p>
                      <DisplayedDate
                        value={displayedTrancheStart(group.tranches, trancheIndex)}
                      />
                      {" al "}<DisplayedDate value={tranche.to} /> ·{" "}
                      {tranche.days} días
                    </p>
                  </div>
                  <strong>{percentage(tranche.percentage)}</strong>
                </header>
                <div className="interest-detail-table">
                  <div className="interest-detail-row heading">
                    <span>Período</span><span>Fórmula</span><span>Resultado</span>
                  </div>
                  {tranche.details.map((detail, index) => (
                    <div className="interest-detail-row" key={`${detail.from}-${index}`}>
                      <span>
                        <DisplayedDate
                          value={index === 0
                            ? displayedTrancheStart(group.tranches, trancheIndex)
                            : detail.from}
                        />
                        {" – "}<DisplayedDate value={detail.to} />
                      </span>
                      <span>{detail.formula}</span>
                      <strong>{percentage(detail.percentage)}</strong>
                    </div>
                  ))}
                </div>
                <footer>
                  <span>Interés del tramo</span>
                  <strong><Money value={tranche.interest} /></strong>
                </footer>
              </article>
            ))}
                </div>
              </section>
            ))}
          </div>
          <p className="interest-source-note">
            Tasas sincronizadas desde la calculadora publicada por el Colegio de Abogados y Procuradores de Mendoza. Verificá la liquidación antes de presentarla.
          </p>
        </section>
      )}

      </div>
    </div>
  );
}
